import apiConfig from './../config/config';
import Cookies from 'js-cookie';
import NotificationManager from '../component/utils/NotificationManager';

//var storageKey = "management_auth_token";
//var accountId = "management_account_id";

class RestClient {
    constructor() {
        // Flag to prevent multiple authentication failure handlers
        this.authFailureHandled = false;
        this.activeRequests = new Set();
        // Store callback for session expiration
        this.onSessionExpired = null;
    }

    // Method to set session expiration callback from AuthWrapper
    setSessionExpiredCallback(callback) {
        this.onSessionExpired = callback;
    }

    // Reset auth failure state (called when user logs in successfully)
    resetAuthState() {
        this.authFailureHandled = false;
        // Clear any existing session-related notifications
        NotificationManager.clearAll();
    }

    DataFormat = Object.freeze({
        JSON: {
            contentType: "application/json",
            method: JSON.stringify
        },
        FORM: {
            contentType:"application/x-www-form-urlencoded"
        //    method: $.param
        },
        RAW: {
            contentType:"application/json",
            method: function(input){
                return input;
            }
        }
    });

    isSessionActive() {
        this.token = Cookies.get("management_auth_token");
        this.accountId = Cookies.get('management_account_id');
        // this is done for dev. on production we have to
        // get it from the Cookie
        //this.token = "b199cbae-384d-498e-80d2-33642b36ac3f";
        if (this.token !== undefined && this.token !== null) {
            this.isAuthenticated = true;
        } else {
            this.isAuthenticated = false;
        }

        return this.isAuthenticated;
    }

    addAuthorizationHeaders(requestHeaders) {
        requestHeaders.set("Authorization", "Bearer " + this.token);
        requestHeaders.set("api-key", apiConfig.STACKRY_API_KEY);
        return requestHeaders;
    }

    createUrl(urlTemplate) {
        return apiConfig.STACKRY_API_SERVER + urlTemplate;
    };

    handleAuthFailure() {
        // Only handle auth failure once to prevent multiple handlers
        if (!this.authFailureHandled) {
            this.authFailureHandled = true;
            
            console.warn('Authentication failed - session expired');
            
            // Cancel all pending requests
            this.activeRequests.forEach(request => {
                if (request.controller) {
                    request.controller.abort();
                }
            });
            this.activeRequests.clear();
            
            // Show session expired notification
            NotificationManager.sessionExpired();
            
            // Call the session expired callback if available
            if (this.onSessionExpired && typeof this.onSessionExpired === 'function') {
                this.onSessionExpired();
            } else {
                // Fallback: clear cookies and reload page
                console.warn('No session expired callback available, clearing cookies and reloading');
                Cookies.remove('management_auth_token');
                Cookies.remove('management_account_id');
                window.location.reload();
            }
        }
    }

    getRequest(urlTemplate, urlParams) {
        // Check if session is active before making request
        if (!this.isSessionActive()) {
            const error = new Error('No valid session - authentication required');
            NotificationManager.warning('Authentication Required', 'Please log in to continue');
            return Promise.reject(error);
        }

        // If auth failure already handled, reject immediately
        if (this.authFailureHandled) {
            return Promise.reject(new Error('Authentication failed - request cancelled'));
        }

        const controller = new window.AbortController();
        const requestId = Date.now() + Math.random();
        
        // Track this request
        this.activeRequests.add({ id: requestId, controller });

        var requestHeaders = new Headers();
        requestHeaders = this.addAuthorizationHeaders(requestHeaders);
        requestHeaders.set("Accept", "application/json");

        var requestSettings = {
            method: 'GET',
            headers: requestHeaders,
            signal: controller.signal
        };

        const self = this;

        function checkStatus(response) {
            if (response.status >= 200 && response.status < 300) {
                return response;
            } else if (response.status === 401) {
                // Authentication failed
                console.warn('API request failed with 401 - token expired or invalid');
                self.handleAuthFailure();
                const error = new Error('Authentication failed - session expired');
                error.response = response;
                error.isAuthError = true;
                throw error;
            } else if (response.status >= 500) {
                // Server error
                console.error(`API request failed with status ${response.status}: ${response.statusText}`);
                NotificationManager.error(
                    'Server Error',
                    'The server encountered an error. Please try again later.',
                    8000
                );
                var error = new Error('Server Error');
                error.response = response;
                error.status = response.status;
                throw error;
            } else if (response.status >= 400) {
                // Client error
                console.warn(`API request failed with status ${response.status}: ${response.statusText}`);
                NotificationManager.warning(
                    'Request Error',
                    'There was a problem with your request. Please check your input and try again.',
                    6000
                );
                var error = new Error('Client Error');
                error.response = response;
                error.status = response.status;
                throw error;
            } else {
                // Other HTTP error
                console.warn(`API request failed with status ${response.status}: ${response.statusText}`);
                var error = new Error(response.statusText);
                error.response = response;
                error.status = response.status;
                throw error;
            }
        }

        function parseJSON(response) {
            return response.json().catch(err => {
                console.warn('Failed to parse JSON response:', err);
                NotificationManager.error(
                    'Response Error',
                    'Received invalid response from server',
                    5000
                );
                throw new Error('Invalid JSON response from server');
            });
        }

        return fetch(this.createUrl(urlTemplate), requestSettings)
                .then(checkStatus)
                .then(parseJSON)
                .then(function(data) {
                    // Remove from active requests on success
                    self.activeRequests.delete([...self.activeRequests].find(r => r.id === requestId));
                    return data;
                }).catch((error) => {
                    // Remove from active requests on error
                    self.activeRequests.delete([...self.activeRequests].find(r => r.id === requestId));
                    
                    // Handle different types of errors
                    if (error.name === 'AbortError') {
                        console.info('Request aborted:', urlTemplate);
                    } else if (error.isAuthError) {
                        // Auth errors are already handled above
                        console.info('Request failed due to authentication:', urlTemplate);
                    } else if (error.name === 'TypeError' || error.message.includes('fetch')) {
                        // Network error
                        console.error('Network error for request:', urlTemplate, error.message);
                        NotificationManager.networkError();
                    } else {
                        // Other errors that weren't already handled
                        if (!error.response || error.response.status < 400) {
                            console.error('API request failed:', urlTemplate, error.message);
                            NotificationManager.apiError(error.message);
                        }
                    }
                    
                    throw error;
                });
    };

    // Enhanced download method with better error handling
    downloadFile = (urlTemplate, containsAdditionalParams) => {
        if (!this.isSessionActive()) {
            console.error('Cannot download file - no valid session');
            NotificationManager.warning('Authentication Required', 'Please log in to download files');
            if (this.onSessionExpired) {
                this.onSessionExpired();
            }
            return;
        }

        var downloadLink = "";
        if (containsAdditionalParams == true) {
            downloadLink = this.createUrl(urlTemplate) + "&bearer_token=" + this.token;
        } else {
            downloadLink = this.createUrl(urlTemplate) + "?bearer_token=" + this.token;
        }

        try {
            window.open(downloadLink, "_blank");
            NotificationManager.info('Download Started', 'Your file download has started', 3000);
        } catch (error) {
            console.error('Failed to open download link:', error);
            NotificationManager.error('Download Failed', 'Unable to start download. Please try again.');
        }
    }

    // Method to test if current token is still valid
    async validateCurrentToken() {
        if (!this.isSessionActive()) {
            return false;
        }

        try {
            const response = await fetch(`${apiConfig.STACKRY_API_SERVER}/oauth2/user`, {
                headers: {
                    'Authorization': `Bearer ${this.token}`,
                    'api-key': apiConfig.STACKRY_API_KEY
                },
            });

            return response.ok;
        } catch (error) {
            console.warn('Token validation failed:', error);
            return false;
        }
    }
}

export default new RestClient();
